/**
 * Owns the "tell us more" half of the result-screen rating: when the detail
 * modal opens, what the guest answered, and getting it onto the session.
 *
 * It sits alongside each page's existing star handler rather than replacing it.
 * The five Result pages that carry a rating each style it differently (Bahela
 * has its own type scale and palette, the default page is layout-tier driven),
 * and unifying those was not worth the regression risk on a screen every paid
 * session ends on. The behaviour that has to be identical everywhere — when to
 * prompt, what a valid answer is, how it is written — lives here.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { isLowRating, toAspectScores, type AspectScore } from '@renderer/lib/feedback/aspects'

export interface SessionFeedback {
  /** Modal visibility. */
  isOpen: boolean
  open: () => void
  /** Dismiss without recording anything. */
  close: () => void
  /** Per-aspect answers, keyed by aspect id; absent/null = not answered. */
  answers: Record<string, number | null>
  setAnswer: (aspectKey: string, score: number) => void
  /** How many aspects carry a usable answer — gates the submit button. */
  answeredCount: number
  /** Persist the answers and close. No-op when nothing was answered. */
  submit: () => void
  /** True once this session's detail feedback has been recorded. */
  submitted: boolean
}

export function useSessionFeedback(rating: number | null): SessionFeedback {
  const { setSessionState, logActivity } = useSessionStateStore()
  const [isOpen, setIsOpen] = useState(false)
  const [answers, setAnswers] = useState<Record<string, number | null>>({})
  const [submitted, setSubmitted] = useState(false)
  /**
   * The auto-prompt fires once per session, not once per low rating.
   *
   * Without this a guest who dismisses the modal and then corrects their tap
   * from 1★ to 2★ gets it thrown at them again — and re-tapping stars while
   * deciding is exactly what an unhappy guest does.
   */
  const autoPromptedRef = useRef(false)

  useEffect(() => {
    if (!isLowRating(rating) || autoPromptedRef.current) return
    autoPromptedRef.current = true
    setIsOpen(true)
  }, [rating])

  const open = useCallback(() => {
    // Opening by hand still counts as the session's prompt, so leaving and
    // re-rating low doesn't re-open on top of the guest.
    autoPromptedRef.current = true
    setIsOpen(true)
  }, [])

  const close = useCallback(() => setIsOpen(false), [])

  const setAnswer = useCallback((aspectKey: string, score: number) => {
    setAnswers((prev) => ({
      // Tapping the star that is already selected clears it — the same
      // affordance the session rating itself has, and the only way back out of
      // an accidental tap on a screen with no undo.
      ...prev,
      [aspectKey]: prev[aspectKey] === score ? null : score
    }))
  }, [])

  const answeredCount = toAspectScores(answers).length

  const submit = useCallback(() => {
    const aspects: AspectScore[] = toAspectScores(answers)
    if (aspects.length === 0) {
      setIsOpen(false)
      return
    }
    const timestamp = new Date()
    setSessionState({
      userPreferences: {
        satisfactionAspects: aspects,
        satisfactionFeedbackAt: timestamp
      }
    })
    logActivity('User rated session aspects', {
      rating,
      aspects,
      timestamp
    })
    setSubmitted(true)
    setIsOpen(false)
  }, [answers, logActivity, rating, setSessionState])

  return { isOpen, open, close, answers, setAnswer, answeredCount, submit, submitted }
}
